/**
 * useScheduleEntities Hook
 *
 * Handles entity logic for Schedule component:
 * - Customer mode detection and transformation
 * - Project-only mode employee extraction
 * - Active entities computation
 */

import { useMemo } from "react";
import type { IServiceCustomer, IServiceEmployee } from "@/services/types";
import type { BookingCardData } from "@/components/booking/display/BookingCard";
import { extractUniqueEmployees } from "@/utils/booking";

export interface UseScheduleEntitiesProps {
  /** Selected employees from filters */
  employees: IServiceEmployee[];
  /** Selected customers from filters */
  customers: IServiceCustomer[];
  /** Selected projects from filters (used for project-only mode detection) */
  projectsCount: number;
  /** Booking cards (used for extracting employees in project-only mode) */
  bookingCards: BookingCardData[];
}

export interface UseScheduleEntitiesResult {
  /** Whether in customer mode (customers selected, no employees) */
  isCustomerMode: boolean;
  /** Active entities to display (employees or transformed customers) */
  activeEntities: IServiceEmployee[];
  /** Map of customer ID to customer (for card data creation) */
  customerById: Map<number, IServiceCustomer>;
}

/**
 * Hook to compute active entities for the schedule view
 *
 * Handles three modes:
 * 1. Employee mode: Display selected employees
 * 2. Customer mode: Transform customers to employee-like objects
 * 3. Project-only mode: Extract employees from booking cards
 */
export function useScheduleEntities({
  employees,
  customers,
  projectsCount,
  bookingCards,
}: UseScheduleEntitiesProps): UseScheduleEntitiesResult {
  // Detect customer mode: customers selected but no employees
  const isCustomerMode = customers.length > 0 && employees.length === 0;

  // Transform customers to employee-like objects for rendering
  const customerEntities = useMemo(
    () =>
      customers.map(
        (customer): IServiceEmployee => ({
          id: customer.id,
          email: "",
          department: null,
          // Prefix with "Kunde:" to match Vue behavior in schedule view
          given_name: `Kunde: ${customer.name}`,
          surname: "",
          name: customer.name,
          is_archived: false,
          settings: { is_bookable: false },
        }),
      ),
    [customers],
  );

  // Project-only mode: when only projects are selected (no employees or customers),
  // extract employees from bookingCards to display rows for employees that have bookings
  const projectOnlyEmployees = useMemo(() => {
    if (employees.length === 0 && customers.length === 0 && projectsCount > 0) {
      return extractUniqueEmployees(bookingCards);
    }
    return [];
  }, [employees.length, customers.length, projectsCount, bookingCards]);

  // Active entities: customer mode uses customers, project-only mode uses extracted employees,
  // otherwise uses the employees prop
  const activeEntities = useMemo(() => {
    if (isCustomerMode) {
      return customerEntities;
    }
    if (projectOnlyEmployees.length > 0) {
      return projectOnlyEmployees;
    }
    return employees;
  }, [isCustomerMode, customerEntities, projectOnlyEmployees, employees]);

  // Map customer ID to customer for card data creation
  const customerById = useMemo(() => {
    const map = new Map<number, IServiceCustomer>();
    customers.forEach((customer) => {
      map.set(customer.id, customer);
    });
    return map;
  }, [customers]);

  return {
    isCustomerMode,
    activeEntities,
    customerById,
  };
}
